import { HTMLInputTypeAttribute } from 'react';
import { create } from 'zustand';
import { CheckboxOption } from '../components/neue-checkboxes';

export type NativeFormElementType = 'checkbox' | 'dropdown' | 'input';

export const NativeFormSupportedElementTypes: NativeFormElementType[] = ['input', 'checkbox', 'dropdown'];

export type NativeFormElementCommonEditableFields = Pick<NativeFormElement, 'label' | 'required'>;

export interface NativeFormConfig {
  title: string;
  description: string;
  submitButtonText: string;
  submitButtonAlignment: 'left' | 'center' | 'right';
}

export type NativeFormCheckboxOption = CheckboxOption;
export type NativeFormDropdownOption = CheckboxOption;

interface NativeFormBase {
  value: string;
  id: string;
  required: boolean;
  label: string;
  description: string;
}

export interface NativeFormBaseWithOptions extends NativeFormBase {
  options: CheckboxOption[];
}

export interface NativeFormCheckboxesInput extends Omit<NativeFormBaseWithOptions, 'value'> {
  type: 'checkbox';
  value: string[];
  orientation: 'vertical' | 'horizontal';
}

export interface NativeFormDropdownInput extends NativeFormBaseWithOptions {
  type: 'dropdown';
}

export interface NativeFormTextInput extends NativeFormBase {
  type: 'input';
  inputType: HTMLInputTypeAttribute;
}

export interface NativeFormElementSpecs {
  uuid: string;
  position: number;
}

type NativeFormElementInputs = Record<NativeFormElement['id'], NativeFormElement> | {};

export type NativeFormElement = (NativeFormCheckboxesInput | NativeFormDropdownInput | NativeFormTextInput) &
  NativeFormElementSpecs;

export type NativeFormElementProps = NativeFormElement & {
  onChange: (element: NativeFormElement) => void;
};

interface Methods {
  setInputElement: (id: NativeFormElement['id'], input: Omit<NativeFormElement, 'id'>) => void;
  replaceInputs: (inputs: NativeFormElementInputs) => void;
  reset: () => void;
}

interface Variables {
  inputs: NativeFormElementInputs;
}

const initialState: Variables = {
  inputs: {},
};

type StoreState = Variables & Methods;

export const useNativeFormStore = create<StoreState>((set) => ({
  ...initialState,
  setInputElement: (id, props) => {
    set((state) => ({
      inputs: {
        ...state.inputs,
        [id]: props,
      },
    }));
  },
  replaceInputs: (inputs) => {
    set(() => ({ inputs }));
  },
  reset: () => set({ ...initialState }),
}));
